import { MessageSquarePlus, MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAIAssistantStore, ChatSession } from '../../stores/aiAssistantStore';
import Button from '../ui/Button';
import { format } from 'date-fns';

interface ChatHistorySidebarProps {
  sessions: ChatSession[];
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
}

export default function ChatHistorySidebar({
  sessions,
  onSessionSelect,
  onNewChat,
}: ChatHistorySidebarProps) {
  const currentSessionId = useAIAssistantStore((state) => state.currentSessionId);
  const chatHistorySidebarOpen = useAIAssistantStore((state) => state.chatHistorySidebarOpen);
  const toggleChatHistorySidebar = useAIAssistantStore((state) => state.toggleChatHistorySidebar);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return 'Today';
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else if (diffInHours < 168) {
        // Less than 7 days
        return format(date, 'EEEE'); // Day name
      } else {
        return format(date, 'MMM d, yyyy');
      }
    } catch {
      return '';
    }
  };

  if (!chatHistorySidebarOpen) {
    return (
      <div className="w-12 border-r border-neutral-200 bg-white flex flex-col items-center py-4 gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleChatHistorySidebar}
          className="h-10 w-10 p-0"
          title="Show chat history"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onNewChat}
          className="h-10 w-10 p-0"
          title="New chat"
        >
          <MessageSquarePlus className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  // Group sessions by date
  const groupedSessions = sessions.reduce((groups, session) => {
    const dateGroup = formatDate(session.createdAt);
    if (!groups[dateGroup]) {
      groups[dateGroup] = [];
    }
    groups[dateGroup].push(session);
    return groups;
  }, {} as Record<string, ChatSession[]>);

  return (
    <aside className="w-64 border-r border-neutral-200 bg-white flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-neutral-900">Chat History</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleChatHistorySidebar}
            className="h-8 w-8 p-0"
            title="Hide chat history"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>

        <Button onClick={onNewChat} className="w-full" size="sm">
          <MessageSquarePlus className="h-4 w-4 mr-2" />
          New Chat
        </Button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto py-2">
        {Object.entries(groupedSessions).length === 0 ? (
          <div className="px-4 py-8 text-center text-neutral-500 text-sm">
            No chat history yet
          </div>
        ) : (
          Object.entries(groupedSessions).map(([dateGroup, groupSessions]) => (
            <div key={dateGroup} className="mb-4">
              <div className="px-4 py-2 text-xs font-medium text-neutral-500 uppercase">
                {dateGroup}
              </div>

              <div className="space-y-1 px-2">
                {groupSessions.map((session) => (
                  <button
                    key={session.sessionId}
                    onClick={() => onSessionSelect(session.sessionId)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                      session.sessionId === currentSessionId
                        ? 'bg-primary-50 text-primary-900'
                        : 'text-neutral-700 hover:bg-neutral-100'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <MessageSquare
                        className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                          session.sessionId === currentSessionId
                            ? 'text-primary-600'
                            : 'text-neutral-400 group-hover:text-neutral-600'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {session.lastMessage || session.title || 'New conversation'}
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                          {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
